import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ProductionJob } from '../../../pot/production-job';
import { CreateStockWithProductionJob } from '../../../pot/pot-jobs/create-stock-dialog/create-stock-with-production-job';
import { CuttingProductionJobService } from '../../cutting-production-job.service';
import { UnitSize } from '../../../../common/unitsize-autocomplete-field/unitsize';
import { PlugType } from '../plug-type.enum';
import {CuttingProductionJob} from '../cutting-production-job';

@Component({
  selector: 'app-printing-dialog',
  templateUrl: './create-plug-stock-dialog.component.html',
  styleUrls: ['./create-plug-stock-dialog.component.scss']
})
export class CreatePlugStockDialogComponent implements OnInit {
  PlugType = PlugType;

  cuttingJob: CuttingProductionJob | null = null;

  originalAmount: number;
  inputMaterialAmount: number;
  inputMaterialAmountTrashed: number;
  remarks: string;
  location: string;
  productionDate: Date;
  bookAutomaticallyFromStock: boolean;
  materialUnitSizeId: number;
  targetUnitSizeId: number;
  selectedPlugType: PlugType = PlugType.TUSSEN;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductionJob, // from resolver
    private dialogRef: MatDialogRef<CreatePlugStockDialogComponent>,
    private cuttingProductionJobService: CuttingProductionJobService,
    private toastrService: ToastrService
  ) {
    const reserved = data.totalReservedAmount ?? 0;

    if (data.hasReservedStock) {
      this.inputMaterialAmount = reserved;
      this.inputMaterialAmountTrashed = 0;
    } else {
      this.inputMaterialAmount = data.orderedAmount ?? 0;
      this.inputMaterialAmountTrashed = 0;
    }

    this.remarks = data.remarks;
    this.originalAmount = data.orderedAmount ?? 0;
    this.bookAutomaticallyFromStock = data.hasReservedStock;
    this.location = '';
    this.productionDate = data.deliveryDate;
    this.materialUnitSizeId = data.materialUnitSizeId;
    this.targetUnitSizeId = data.targetUnitSizeId;
  }

  ngOnInit(): void {
    this.loadCuttingJob();
  }

  private loadCuttingJob(): void {
    this.cuttingProductionJobService.findById(this.data.id).subscribe({
      next: cuttingJob => {
        this.cuttingJob = cuttingJob;

        if (cuttingJob.plugType != null) {
          this.selectedPlugType = cuttingJob.plugType;
        }
      },
      error: err => {
        console.error('Error loading cutting job', err);
      }
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private normalizeToZero(value: any): number {
    const num = Number(value);
    if (isNaN(num) || value === null || value === undefined || value === '') {
      return 0;
    }
    return num;
  }

  async onSubmitClick() {
    const newStockEntity = new CreateStockWithProductionJob(
      this.normalizeToZero(this.originalAmount),
      this.location,
      this.remarks,
      this.data.id,
      this.productionDate,
      this.normalizeToZero(this.inputMaterialAmount),
      this.normalizeToZero(this.inputMaterialAmountTrashed),
      this.bookAutomaticallyFromStock,
      this.targetUnitSizeId,
      this.materialUnitSizeId,
      this.selectedPlugType
    );

    try {
      await this.cuttingProductionJobService
        .createNewStockEntity(newStockEntity)
        .toPromise();

      this.toastrService.success('Nieuwe voorraad succesvol aangemaakt!');
      this.dialogRef.close({ success: true });
    } catch (e) {
      this.toastrService.error('Er is iets mis gegaan!');
      console.error(e);
    }
  }

  isDisabled() {
    if (this.normalizeToZero(this.originalAmount) <= 0 || !this.location || this.location.trim().length === 0) {
      return true;
    }

    if (this.data.hasReservedStock) {
      const reserved = this.data.totalReservedAmount ?? 0;
      const used = this.normalizeToZero(this.inputMaterialAmount);
      const trashed = this.normalizeToZero(this.inputMaterialAmountTrashed);

      if (used + trashed > reserved) {
        return true;
      }
    }

    return false;
  }

  setMaterialUnitSize($event: UnitSize) {
    this.materialUnitSizeId = $event.id;
  }

  setTargetUnitSize($event: UnitSize) {
    this.targetUnitSizeId = $event.id;
  }

  onInputMaterialUsedChanged(value: number | null) {
    const reserved = this.data.totalReservedAmount ?? 0;
    let used = this.normalizeToZero(value);

    if (this.data.hasReservedStock) {
      if (used < 0) {
        used = 0;
      }
      if (used > reserved) {
        used = reserved;
      }
      this.inputMaterialAmount = used;
      this.inputMaterialAmountTrashed = reserved - used;
    } else {
      if (used < 0) {
        used = 0;
      }
      this.inputMaterialAmount = used;
    }
  }

  onTrashedInput(event: Event) {
    const reserved = this.data.totalReservedAmount ?? 0;
    const inputEl = event.target as HTMLInputElement;

    let trashed = this.normalizeToZero(inputEl.value);

    if (this.data.hasReservedStock) {
      if (trashed < 0) {
        trashed = 0;
      }

      const used = this.normalizeToZero(this.inputMaterialAmount);
      const maxTrashedGivenUsed = Math.max(0, reserved - used);

      if (trashed > maxTrashedGivenUsed) {
        trashed = maxTrashedGivenUsed;
      }
    } else {
      if (trashed < 0) {
        trashed = 0;
      }
    }

    this.inputMaterialAmountTrashed = trashed;
    inputEl.value = String(trashed);
  }

  onOriginalAmountChanged(value: number | null) {
    this.originalAmount = this.normalizeToZero(value);
  }

  onPlugTypeChange(value: PlugType) {
    this.selectedPlugType = value;
  }
}
